export interface CompressionResult {
  blob: Blob;
  originalSize: number;
  compressedSize: number;
  compressionRatio: number;
  width: number;
  height: number;
}

export async function compressImage(
  file: File,
  maxWidth: number = 1920,
  maxSizeMB: number = 0.5,
  format: 'webp' | 'jpeg' = 'webp',
  quality: number = 0.85
): Promise<CompressionResult> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const reader = new FileReader();

    reader.onload = (e) => {
      img.src = e.target?.result as string;
    };

    reader.onerror = () => reject(new Error('Failed to read file'));

    img.onload = async () => {
      try {
        let canvas = document.createElement('canvas');
        let ctx = canvas.getContext('2d')!;

        let { width, height } = img;

        if (width > maxWidth) {
          height = (height * maxWidth) / width;
          width = maxWidth;
        }

        canvas.width = width;
        canvas.height = height;

        ctx.drawImage(img, 0, 0, width, height);

        const mimeType = format === 'webp' ? 'image/webp' : 'image/jpeg';
        let currentQuality = quality;
        let blob: Blob | null = null;

        const targetSize = maxSizeMB * 1024 * 1024;

        for (let attempts = 0; attempts < 5; attempts++) {
          blob = await new Promise<Blob | null>((resolve) => {
            canvas.toBlob(
              (b) => resolve(b),
              mimeType,
              currentQuality
            );
          });

          if (!blob) {
            throw new Error('Failed to create blob');
          }

          if (blob.size <= targetSize || currentQuality <= 0.3) {
            break;
          }

          currentQuality -= 0.1;
        }

        if (!blob) {
          throw new Error('Failed to compress image');
        }

        const result: CompressionResult = {
          blob,
          originalSize: file.size,
          compressedSize: blob.size,
          compressionRatio: (1 - blob.size / file.size) * 100,
          width,
          height,
        };

        resolve(result);
      } catch (error) {
        reject(error);
      }
    };

    img.onerror = () => reject(new Error('Failed to load image'));

    reader.readAsDataURL(file);
  });
}

export function formatBytes(bytes: number, decimals: number = 2): string {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];

  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
}